import { useCallback, useEffect, useState } from "react";

const chromeExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;

interface UseChromeExtensionReturn {
  isExtensionAvailable: boolean;
  isCheckingExtension: boolean;
  isExtensionError: boolean;
}

export default function useChromeExtension(): UseChromeExtensionReturn {
  const [isExtensionAvailable, setIsExtensionAvailable] = useState(false);
  const [isCheckingExtension, setIsCheckingExtension] = useState(true);

  const checkExtension = useCallback(() => {
    if (typeof window === "undefined" || !window.chrome?.runtime?.sendMessage) {
      setIsExtensionAvailable(false);
      setIsCheckingExtension(false);
      return;
    }

    try {
      window.chrome.runtime.sendMessage(
        chromeExtensionId,
        { command: "ping" },
        (response) => {
          const lastError = window.chrome.runtime.lastError;

          if (lastError) {
            // Extension not installed or unreachable
            setIsExtensionAvailable(false);
          } else if (response?.pong === true) {
            // Expected valid response
            setIsExtensionAvailable(true);
          } else {
            // Extension responded, but not with expected payload
            console.warn("Unexpected extension response:", response);
            setIsExtensionAvailable(true); // Extension exists, but protocol mismatch
          }

          setIsCheckingExtension(false);
        },
      );
    } catch (err) {
      console.error("Error while checking Chrome extension:", err);
      setIsExtensionAvailable(false);
      setIsCheckingExtension(false);
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkExtension();

    // Re-check when user focuses the window (common when extension is installed/uninstalled)
    window.addEventListener("focus", checkExtension);

    // Attach management listeners if available
    if (window.chrome?.management) {
      const handleInstall = (info: chrome.management.ExtensionInfo) => {
        if (info.id === chromeExtensionId) checkExtension();
      };
      const handleUninstall = (id: string) => {
        if (id === chromeExtensionId) checkExtension();
      };

      window.chrome.management.onInstalled.addListener(handleInstall);
      window.chrome.management.onUninstalled.addListener(handleUninstall);

      return () => {
        window.removeEventListener("focus", checkExtension);
        window.chrome?.management?.onInstalled?.removeListener(handleInstall);
        window.chrome?.management?.onUninstalled?.removeListener(
          handleUninstall,
        );
      };
    }

    return () => {
      window.removeEventListener("focus", checkExtension);
    };
  }, []);

  const isExtensionError = !isCheckingExtension && !isExtensionAvailable;

  return { isExtensionAvailable, isCheckingExtension, isExtensionError };
}
